import React, { useMemo } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import { buildAxis, computeDateBounds, groupRowsIntoSections, offsetPercent, ragTone } from './viewModeUtils';

import StarBadgeIcon from 'Assets/icons/star-badge.svg';

const BLANK_PLACEHOLDER = '—';

// The date a milestone pill sits on: its target date, falling back to its start
// date so a row with only a start is still placed rather than dropped.
const pillDate = (row) => row.target_completion_date || row.start_date;

// A row is "undated" only when it has neither a start nor a target date.
const isUndated = (row) => !row.start_date && !row.target_completion_date;

const toneDotClass = (tone) =>
    classNames('tw-inline-block tw-w-2 tw-h-2 tw-rounded-full tw-shrink-0', {
        '!tw-bg-red-500': tone === 'red',
        '!tw-bg-yellow-400': tone === 'amber',
        '!tw-bg-green-500': tone === 'green',
        'tw-bg-charcoal-25': tone === 'neutral',
    });

const Pill = ({ row, bounds }) => {
    const tone = ragTone(row);
    const left = offsetPercent(pillDate(row), bounds);

    return (
        <div
            className="tw-absolute tw-top-1/2 tw--translate-y-1/2 tw-max-w-48"
            style={{ insetInlineStart: `${left}%`, transform: 'translate(-50%, -50%)' }}
            title={`${row.description} · ${moment(pillDate(row)).format('DD-MM-YYYY')}`}
        >
            <span className="tw-inline-flex tw-items-center tw-gap-x-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-2xs tw-font-medium tw-bg-white tw-border tw-border-grey tw-whitespace-nowrap">
                {row.is_key_milestone && <StarBadgeIcon className="tw-h-2.5 tw-w-2.5 tw-text-blue tw-shrink-0" />}
                <span className={toneDotClass(tone)} />
                <span className="tw-truncate">{row.description}</span>
            </span>
        </div>
    );
};

/**
 * Timeline view (IB-674).
 *
 * Category swim-lanes down the side, a period axis across the top, and a pill
 * per activity positioned on its target date. Key milestones carry a star; RAG
 * status drives the pill dot. Rows with no start AND no target date are listed
 * separately as "Undated". Read-only, on-screen only.
 */
const TimelineView = ({ rows, timeView }) => {
    const bounds = useMemo(() => computeDateBounds(rows), [rows]);
    const sections = useMemo(() => groupRowsIntoSections(rows), [rows]);

    // Multi-year Days guard (shared with GanttView + the parent notice).
    const axis = useMemo(() => buildAxis(bounds, timeView), [bounds, timeView]);

    const datedSections = useMemo(
        () => sections.map((section) => ({ ...section, rows: section.rows.filter((row) => !isUndated(row)) })).filter((section) => section.rows.length > 0),
        [sections],
    );

    const undatedRows = useMemo(() => (rows || []).filter((row) => !row._is_group && isUndated(row)), [rows]);

    const hasAxis = !!bounds && axis.columns.length > 0;

    // A fully date-less filtered set (bounds null) must still surface its
    // activities in the Undated list — never drop them behind the empty state.
    if (!hasAxis && undatedRows.length === 0) {
        return (
            <div className="tw-p-6 tw-text-sm tw-text-charcoal-75 tw-bg-grey/40 tw-rounded-lg">
                {$t('No dated activities to plot on the timeline for the current criteria.')}
            </div>
        );
    }

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-4">
            {hasAxis && (
                <div className="tw-border tw-border-grey tw-rounded-lg tw-overflow-hidden">
                    {/* Period axis */}
                    <div className="tw-flex tw-bg-background-2 tw-border-b tw-border-grey">
                        <div className="tw-w-48 tw-shrink-0 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-charcoal tw-border-e tw-border-grey">
                            {$t('Category')}
                        </div>
                        <div className="tw-flex tw-flex-auto">
                            {axis.columns.map((column) => (
                                <div
                                    key={column.key}
                                    className="tw-flex-1 tw-px-1 tw-py-2 tw-text-center tw-text-2xs tw-font-medium tw-text-charcoal-75 tw-border-e last:tw-border-e-0 tw-border-grey tw-truncate"
                                >
                                    {column.label}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Swim-lanes */}
                    {datedSections.map((section, sectionIndex) => (
                        <div key={`lane-${sectionIndex}`} className="tw-flex tw-border-b last:tw-border-b-0 tw-border-grey">
                            <div className="tw-w-48 tw-shrink-0 tw-px-3 tw-py-3 tw-text-xs tw-font-medium tw-text-charcoal tw-bg-charcoal-15 tw-border-e tw-border-grey tw-break-words">
                                {section.label || $t('Uncategorised')}
                            </div>
                            <div className="tw-relative tw-flex-auto tw-min-h-16">
                                {/* Column gridlines */}
                                <div className="tw-absolute tw-inset-0 tw-flex tw-pointer-events-none">
                                    {axis.columns.map((column) => (
                                        <div key={column.key} className="tw-flex-1 tw-border-e last:tw-border-e-0 tw-border-grey/60" />
                                    ))}
                                </div>
                                {section.rows.map((row, rowIndex) => (
                                    <Pill key={row.id ?? `${sectionIndex}-${rowIndex}`} row={row} bounds={bounds} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {undatedRows.length > 0 && (
                <div className="tw-border tw-border-grey tw-rounded-lg tw-p-4">
                    <div className="tw-text-xs tw-font-semibold tw-text-charcoal-75 tw-mb-2">
                        {$t('Undated')} ({undatedRows.length})
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                        {undatedRows.map((row, index) => (
                            <span
                                key={row.id ?? index}
                                className="tw-inline-flex tw-items-center tw-gap-x-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-2xs tw-font-medium tw-bg-grey/50 tw-text-charcoal"
                                title={row.description}
                            >
                                {row.is_key_milestone && <StarBadgeIcon className="tw-h-2.5 tw-w-2.5 tw-text-blue tw-shrink-0" />}
                                <span className="tw-truncate tw-max-w-48">{row.description || BLANK_PLACEHOLDER}</span>
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimelineView;
